/**
 * `/migrate` — move one legacy NSS IT App ticket into the ITSM.
 *
 * Four steps, held in component state: Find → Summary → Review → Done. The
 * lookup and the summary write nothing; only Import does, once, and a legacy
 * ticket that was already imported stops at the Summary with a link to its
 * ITSM ticket. Migrators and admins only (`<MigratorRoute>` here, and every
 * `/migrate/*` endpoint independently).
 */

import { useState } from "react";

import { AppBreadcrumbs, PageContainer } from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { LegacySummary, MigrateImportResponse } from "@/lib/api";
import { logger } from "@/lib/logger";

import { ConnectBanner } from "./components/connect-banner";
import { DoneStep } from "./components/done-step";
import { HistoryList } from "./components/history-list";
import { LookupStep } from "./components/lookup-step";
import { ReviewIncident } from "./components/review-incident";
import { ReviewRequest } from "./components/review-request";
import { SummaryStep } from "./components/summary-step";
import type { Classification, Customer } from "./lib/migrate-helpers";

type Step =
  | { name: "lookup" }
  | { name: "summary"; summary: LegacySummary }
  | { name: "review"; summary: LegacySummary; classification: Classification; customer: Customer }
  | { name: "done"; result: MigrateImportResponse };

export function MigratePage() {
  const { t } = useT("tickets");
  useDocumentTitle(t("migrate.title"));
  const [step, setStep] = useState<Step>({ name: "lookup" });
  // Bumped on "Migrate another" so the lookup form starts empty.
  const [round, setRound] = useState(0);

  const again = () => {
    setRound((previous) => previous + 1);
    setStep({ name: "lookup" });
  };

  return (
    <PageContainer
      title={t("migrate.title")}
      subtitle={t("migrate.subtitle")}
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("migrate.title") }]} />}
    >
      <div className="flex flex-col gap-6">
        <ConnectBanner />
        {step.name === "lookup" ? (
          <>
            <LookupStep
              key={round}
              onFound={(summary) => {
                logger.debug("[migrate] summary", summary.legacy_number);
                setStep({ name: "summary", summary });
              }}
            />
            <HistoryList />
          </>
        ) : null}
        {step.name === "summary" ? (
          <SummaryStep
            summary={step.summary}
            onBack={again}
            onOk={(classification, customer) =>
              setStep({ name: "review", summary: step.summary, classification, customer })
            }
          />
        ) : null}
        {step.name === "review" ? (
          step.classification.kind === "IN" ? (
            <ReviewIncident
              summary={step.summary}
              classification={step.classification}
              customer={step.customer}
              onBack={() => setStep({ name: "summary", summary: step.summary })}
              onDone={(result) => setStep({ name: "done", result })}
            />
          ) : (
            <ReviewRequest
              summary={step.summary}
              classification={step.classification}
              customer={step.customer}
              onBack={() => setStep({ name: "summary", summary: step.summary })}
              onDone={(result) => setStep({ name: "done", result })}
            />
          )
        ) : null}
        {step.name === "done" ? <DoneStep result={step.result} onAnother={again} /> : null}
      </div>
    </PageContainer>
  );
}
